"use client";

import React, { useCallback } from "react";
import { ChromePicker, ColorResult } from "react-color";
import styled from "styled-components";
import { Editor } from "@tiptap/core";

import PopupMenu from "../../PopupMenu";
import Box from "../../Box";
import useStoredState from "../../../hooks/useStoredState";

const StyledColorPicker = styled(ChromePicker)`
  width: 100% !important;
`;

const RECENT_COLOR_COUNT = 3;

type Props = {
  editor: Editor;
};

const TextColorOptions: React.FC<Props> = ({ editor }) => {
  const activeColor = (editor.getAttributes("textColor")?.color as string) || null;
  const pickerColor = activeColor?.startsWith("#") ? activeColor : "#000000";

  const [recentColors, setRecentColors] = useStoredState<string[]>(`text-color-recent-colors`, []);

  const applyTextColor = useCallback(
    (color: string) => {
      editor.chain().focus().setTextColor(color).run();
    },
    [editor]
  );

  const removeTextColor = useCallback(() => {
    editor.chain().focus().unsetTextColor().run();
  }, [editor]);

  const onCustomColorChange = useCallback(
    (color: ColorResult) => {
      applyTextColor(color.hex);
      setRecentColors(prev => {
        const upper = color.hex.toUpperCase();
        const newColors = [upper, ...prev.filter(c => c !== upper)];
        return newColors.slice(0, RECENT_COLOR_COUNT);
      });
    },
    [applyTextColor, setRecentColors]
  );

  return (
    <>
      <PopupMenu.Section title="Text color">
        <PopupMenu.Item
          key="default"
          title="Default"
          onClick={removeTextColor}
          checked={!activeColor}
          image={{ color: "transparent" }}
        />
      </PopupMenu.Section>
      <PopupMenu.Section title={recentColors.length > 0 ? "Recent colors" : null}>
        {recentColors.length > 0 && (
          <>
            {recentColors.map(color => (
              <PopupMenu.Item
                key={color}
                title={color}
                onClick={() => applyTextColor(color)}
                checked={activeColor === color}
                image={{ color }}
              />
            ))}
          </>
        )}
        <PopupMenu.Item
          key="custom"
          title="Custom"
          image={{ iconId: "content.custom-colors", fill: "", size: "16" }}
          checked={activeColor?.startsWith("#") && !recentColors.includes(activeColor)}>
          <Box onClick={e => e.stopPropagation()}>
            <StyledColorPicker
              color={pickerColor}
              onChangeComplete={onCustomColorChange}
              disableAlpha
            />
          </Box>
        </PopupMenu.Item>
      </PopupMenu.Section>
    </>
  );
};

export default TextColorOptions;
